import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type {
  AIGenerationChatMessage,
  AIGenerationConversation,
  GptLogInputSnapshotRow,
} from "@/integrations/backend/program-generation";
import { GptLogDetailDialog, GptLogInputsDialog } from "./gpt-log-debug-dialogs";

export function collectGptLogDebugInfo(messages: AIGenerationChatMessage[]) {
  const orderedIds: string[] = [];
  const seen = new Set<string>();
  const snapshotByLogId: Record<string, GptLogInputSnapshotRow[]> = {};
  for (const m of messages) {
    if (m.role !== "assistant") continue;
    for (const id of m.gpt_log_ids ?? []) {
      if (!seen.has(id)) {
        seen.add(id);
        orderedIds.push(id);
      }
      const snap = m.gpt_log_input_snapshots?.[id];
      if (snap?.length) snapshotByLogId[id] = snap;
    }
  }
  return { orderedLogIds: orderedIds, snapshotByLogId };
}

type Props = {
  conversation: AIGenerationConversation | null;
  busy: boolean;
  className?: string;
};

export default function AIGenerationDebugSidebar({ conversation, busy, className = "" }: Props) {
  const [rawLogId, setRawLogId] = useState<string | null>(null);
  const [inputsDialog, setInputsDialog] = useState<{
    logId: string;
    rows: GptLogInputSnapshotRow[] | undefined;
  } | null>(null);

  const messages = conversation?.messages ?? [];
  const debugTrace = conversation?.generation_debug_trace ?? [];
  const { orderedLogIds, snapshotByLogId } = useMemo(
    () => collectGptLogDebugInfo(messages),
    [messages]
  );

  return (
    <>
      <div
        className={`flex min-h-0 min-w-0 flex-col border border-gray-200 bg-gray-100 lg:max-h-[60vh] ${className}`}
      >
        <div className="flex-shrink-0 border-b border-gray-300 bg-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-900">
          Backend activity
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto border-b border-gray-200 bg-gray-50 p-3">
          {busy && debugTrace.length === 0 ? (
            <p className="text-xs text-gray-800">Polling… waiting for backend trace lines.</p>
          ) : null}
          {!busy && debugTrace.length === 0 ? (
            <p className="text-xs text-gray-600">
              Detailed steps appear here while generation runs when debugger mode is on.
            </p>
          ) : null}
          {debugTrace.length > 0 ? (
            <ol className="list-decimal space-y-1 pl-4 text-xs text-gray-900">
              {debugTrace.map((entry, idx) => (
                <li key={`${entry.at}-${idx}`} className="marker:text-gray-400">
                  <span className="font-mono text-[10px] text-gray-500">
                    {entry.at
                      ? `${new Date(entry.at).toLocaleTimeString(undefined, {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        })} `
                      : null}
                  </span>
                  <span className="break-words font-mono">{entry.message}</span>
                </li>
              ))}
            </ol>
          ) : null}
        </div>
        <div className="flex-shrink-0 border-b border-gray-300 bg-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-900">
          GPT runs
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50 p-3">
          {orderedLogIds.length === 0 ? (
            <p className="text-xs text-gray-800">IDs appear after the model responds.</p>
          ) : (
            <ul className="space-y-2">
              {orderedLogIds.map((id) => (
                <li
                  key={id}
                  className="flex flex-wrap items-center gap-2 rounded-md border border-gray-300 bg-white px-2 py-1.5 shadow-sm"
                >
                  <button
                    type="button"
                    className="rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 font-mono text-xs font-medium text-gray-950 hover:bg-gray-200"
                    onClick={() => setRawLogId(id)}
                  >
                    {id.slice(0, 8)}…
                  </button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setInputsDialog({ logId: id, rows: snapshotByLogId[id] })}
                  >
                    Inputs
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
        {busy ? (
          <div className="flex flex-shrink-0 items-center gap-2 border-t border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Generation in progress…
          </div>
        ) : null}
      </div>
      <GptLogDetailDialog
        open={rawLogId !== null}
        onOpenChange={(next) => {
          if (!next) setRawLogId(null);
        }}
        logId={rawLogId}
      />
      <GptLogInputsDialog
        open={inputsDialog !== null}
        onOpenChange={(next) => {
          if (!next) setInputsDialog(null);
        }}
        logId={inputsDialog?.logId ?? null}
        rows={inputsDialog?.rows}
      />
    </>
  );
}
